import ChannelList from '../components/ChannelList';
import React, { useState, useEffect, useMemo, useRef } from "react";
import { usePlayer } from "../context/PlayerContext";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  MagnifyingGlass, Fire, X, Broadcast, MapPin, ArrowRight,
  Download, DeviceMobile, ClockCounterClockwise
} from "@phosphor-icons/react";
import {
  searchStations,
  getStationsByCountryCode,
  trendingStations,
  topStations,
  getCountries,
  getLanguages,
  getLanguagesByCountry,
  getUserCountry,
} from "../lib/api";

import { StationGrid } from "../components/StationGrid";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const COUNTRY_CHIPS = [
  { label: "Australia", iso: "AU" },
  { label: "New Zealand", iso: "NZ" },
  { label: "UK", iso: "GB" },
  { label: "India", iso: "IN" },
  { label: "U.S.", iso: "US" },
  { label: "Ireland", iso: "IE" },
  { label: "Spain", iso: "ES" },
  { label: "Canada", iso: "CA" },
  { label: "Germany", iso: "DE" },
  { label: "France", iso: "FR" },
];

const ALLOWED_ISOS = new Set(["AU", "NZ", "GB", "IN", "US", "IE", "ES", "CA", "DE", "FR"]);

function isAllowedStation(station) {
  if (!station) return false;
  const code = String(station.countrycode || station.countryCode || "").toUpperCase();
  if (ALLOWED_ISOS.has(code)) return true;
  const name = String(station.country || "").toLowerCase().trim();
  const nameToIso = {
    "australia": "AU",
    "new zealand": "NZ",
    "united kingdom": "GB",
    "uk": "GB",
    "great britain": "GB",
    "india": "IN",
    "united states": "US",
    "united states of america": "US",
    "usa": "US",
    "america": "US",
    "ireland": "IE",
    "republic of ireland": "IE",
    "spain": "ES",
    "canada": "CA",
    "germany": "DE",
    "france": "FR",
  };
  return ALLOWED_ISOS.has(nameToIso[name] || "");
}

function useDebounce(value, delay = 450) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function getFlagEmoji(code) {
  if (!code || code.length !== 2) return "";
  return code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

export default function Discover() {
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("all");
  const [language, setLanguage] = useState("all");
  const [showAllPopular, setShowAllPopular] = useState(false);
  const [showAllFiltered, setShowAllFiltered] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const debouncedSearch = useDebounce(search);
  const { history } = usePlayer();

  const { data: countries } = useQuery({ queryKey: ["countries"], queryFn: getCountries });
  const { data: trending } = useQuery({ queryKey: ["trending"], queryFn: () => trendingStations(20) });

  const allowedCountries = useMemo(
    () => (countries || []).filter((c) => ALLOWED_ISOS.has(String(c.iso_3166_1 || "").toUpperCase())),
    [countries]
  );

  const { data: popularStations, isLoading: popularLoading } = useQuery({
    queryKey: ["topStations20"],
    queryFn: () => topStations(8),
    staleTime: 5 * 60 * 1000,
  });

  const selectedCountry = useMemo(() => {
    if (country === "all" || !allowedCountries.length) return null;
    return allowedCountries.find((c) => c.name === country) || null;
  }, [country, allowedCountries]);

  const { data: languages } = useQuery({
    queryKey: ["languages", country, selectedCountry?.iso_3166_1],
    queryFn: () =>
      country !== "all"
        ? getLanguagesByCountry({ country, countrycode: selectedCountry?.iso_3166_1 })
        : getLanguages(40),
    enabled: country === "all" || !!selectedCountry,
    staleTime: 5 * 60 * 1000,
  });

  const { data: countryData } = useQuery({
    queryKey: ["userCountry"],
    queryFn: getUserCountry,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  const { data: nearbyStations, isLoading: nearbyLoading } = useQuery({
    queryKey: ["nearbyStations", countryData?.countryCode],
    queryFn: () =>
      searchStations({
        countrycode: countryData.countryCode,
        order: "votes",
        reverse: true,
        limit: 8,
        hidebroken: "true",
      }),
    enabled: !!countryData?.countryCode && ALLOWED_ISOS.has(String(countryData.countryCode).toUpperCase()),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    setLanguage("all");
  }, [country]);

  const hasFilters = Boolean(debouncedSearch) || country !== "all" || language !== "all";

  useEffect(() => {
    if (hasFilters) setShowAllPopular(false);
  }, [hasFilters]);

  useEffect(() => {
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => {
      setShowInstall(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const PAGE = 40;
  const [offset, setOffset] = useState(0);
  const [stations, setStations] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(true);

  useEffect(() => {
    setOffset(0);
    setShowAllFiltered(false);
  }, [debouncedSearch, country, language]);

  const { data: pageData, isFetching } = useQuery({
    queryKey: ["stations", debouncedSearch, country, language, offset],
    queryFn: async () => {
      if (country !== "all" && selectedCountry?.iso_3166_1 && !debouncedSearch && language === "all") {
        return getStationsByCountryCode(selectedCountry.iso_3166_1, {
          offset,
          limit: PAGE,
          order: "votes",
          reverse: true,
          hidebroken: true,
        });
      }

      return searchStations({
        name: debouncedSearch || undefined,
        country: country !== "all" ? country : undefined,
        language: language !== "all" ? language : undefined,
        order: "votes",
        reverse: true,
        limit: PAGE,
        offset,
      });
    },
    placeholderData: (prev) => prev,
    enabled: hasFilters || showAllPopular,
  });

  useEffect(() => {
    if (!pageData) return;
    const rawList = Array.isArray(pageData) ? pageData : [];
    const list = rawList.filter(isAllowedStation);
    setHasNextPage(rawList.length === PAGE);
    setStations((prev) => {
      const base = offset === 0 ? [] : prev;
      const seen = new Set(base.map((s) => s.stationuuid));
      const merged = [...base];
      list.forEach((s) => {
        if (!seen.has(s.stationuuid)) {
          seen.add(s.stationuuid);
          merged.push(s);
        }
      });
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData]);

  const isLoading = isFetching && offset === 0 && stations.length === 0;
  const isFetchingNextPage = isFetching && offset > 0;

  const fetchNextPage = () => setOffset((o) => o + PAGE);

  const loadMoreRef = useRef(null);
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage();
        }
      },
      { rootMargin: "400px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNextPage, isFetching, offset, showAllFiltered]);

  const trendingNames = useMemo(
    () =>
      (trending || [])
        .filter(isAllowedStation)
        .map((s) => s.name?.trim())
        .filter(Boolean)
        .slice(0, 16),
    [trending]
  );

  const activeChipIso = useMemo(() => {
    if (!selectedCountry) return null;
    const chip = COUNTRY_CHIPS.find((c) => c.iso === selectedCountry.iso_3166_1);
    return chip ? chip.iso : null;
  }, [selectedCountry]);

  const countryLanguageChips = useMemo(
    () => (languages || []).filter((l) => l.name),
    [languages]
  );

  const handleCountryChip = (iso) => {
    if (!countries) return;
    const match = countries.find((c) => c.iso_3166_1 === iso);
    if (!match) return;
    setCountry((prev) => (prev === match.name ? "all" : match.name));
  };

  const clearAll = () => {
    setSearch("");
    setCountry("all");
    setLanguage("all");
    setShowAllPopular(false);
  };

  useEffect(() => {
    if (!location.state?.resetHomeAt) return;
    clearAll();
    setOffset(0);
    setStations([]);
    navigate("/", { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.resetHomeAt]);

  const handleSeeAllPopular = () => {
    setOffset(0);
    setStations([]);
    setShowAllPopular(true);
  };

  const handleSeeAllNearby = () => {
    if (countryData?.countryCode && countries) {
      const match = countries.find((c) => c.iso_3166_1 === countryData.countryCode);
      if (match) {
        setCountry(match.name);
        return;
      }
    }
    if (countryData?.countryName) {
      setCountry(countryData.countryName);
    }
  };

  const showSections = !hasFilters && !showAllPopular;

  const emptyState = (
    <div className="rounded-2xl border border-border bg-surface py-20 text-center">
      <p className="font-heading text-xl text-foreground">No stations found</p>
      <p className="mt-2 text-sm text-muted2">Try a different search or clear your filters.</p>
    </div>
  );

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShowInstall(false);
    }
    setDeferredPrompt(null);
  };

  const filteredPopular = useMemo(() => (popularStations || []).filter(isAllowedStation).slice(0, 8), [popularStations]);
  const filteredNearby = useMemo(() => (nearbyStations || []).filter(isAllowedStation).slice(0, 8), [nearbyStations]);
  const filteredHistory = useMemo(() => (Array.isArray(history) ? history : []).filter(isAllowedStation).slice(0, 8), [history]);

  const userCountryAllowed = Boolean(
    countryData?.countryCode && ALLOWED_ISOS.has(String(countryData.countryCode).toUpperCase())
  );

  return (
    <div className="space-y-10">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-border bg-surface px-6 py-12 md:px-12 md:py-16">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/27141312/pexels-photo-27141312.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-transparent" />

        {trendingNames.length > 0 && (
          <Marquee speed={40} gradient={false} className="absolute top-6 left-0 right-0 overflow-hidden">
            <span className="text-stroke whitespace-nowrap font-heading text-7xl font-bold uppercase tracking-tight md:text-8xl">
              {trendingNames.join("  •  ")}  •
            </span>
          </Marquee>
        )}

        <div className="relative mt-20 max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">
            {allowedCountries.length ? `${allowedCountries.length} countries` : "Streaming worldwide"} · live
          </p>
          <h1 className="mt-3 font-heading text-4xl font-light tracking-tighter text-foreground sm:text-5xl lg:text-6xl">
            Tune into the world's <span className="text-gold">frequencies</span>
          </h1>
          <h1 className="mt-3 font-heading text-4xl font-light tracking-tighter text-foreground sm:text-5xl lg:text-6xl">
            Tune into the world's <span className="text-gold">frequencies</span>
          </h1>

          {showInstall && !isIOS && (
            <div className="mt-5 flex items-center gap-3 rounded-2xl border border-border bg-surface/80 p-3 backdrop-blur-sm">
              <img
                src="/icon-192.png"
                alt="RadioHub"
                className="h-12 w-12 rounded-xl object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <div className="flex-1 min-w-0">
                <p className="font-heading text-sm font-medium text-foreground">RadioHub</p>
                <p className="text-xs text-muted2">Install for quick access</p>
              </div>
              <button
                onClick={handleInstallClick}
                className="flex items-center gap-1.5 rounded-full bg-gold px-4 py-2 font-mono text-xs font-medium text-black transition-opacity hover:opacity-90"
              >
                <Download size={14} weight="bold" />
                Install
              </button>
              <button
                onClick={() => setShowInstall(false)}
                className="p-1 text-muted2 transition-colors hover:text-foreground"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {isIOS && (
            <div className="mt-5 flex items-center gap-3 rounded-2xl border border-border bg-surface/80 p-3 backdrop-blur-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/10">
                <DeviceMobile size={24} className="text-gold" />
              </div>
              <div className="flex-1">
                <p className="font-heading text-sm font-medium text-foreground">Install RadioHub</p>
                <p className="text-xs text-muted2">
                  Tap Share → Add to Home Screen
                </p>
              </div>
            </div>
          )}

          <p className="mt-4 max-w-lg text-base font-light leading-relaxed text-muted2">
            Thousands of live radio stations from every corner of the globe. Search, discover, and save your favorites.
          </p>

          <div className="relative mt-7 max-w-xl">
            <MagnifyingGlass size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-muted2" />
            <input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search stations by name..."
              className="w-full rounded-full border border-border bg-black/40 py-3.5 pl-12 pr-5 text-foreground outline-none backdrop-blur-sm transition-all placeholder:text-muted2 focus:border-gold focus:ring-1 focus:ring-gold/50"
            />
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger data-testid="country-filter" className="w-[180px] rounded-full border-border bg-surface">
              <SelectValue placeholder="Country" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All Countries</SelectItem>
              {allowedCountries.map((c) => (
                <SelectItem key={c.name} value={c.name}>
                  {c.name} ({c.stationcount})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger data-testid="language-filter" className="w-[180px] rounded-full border-border bg-surface">
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All Languages</SelectItem>
              {(languages || []).map((l) => (
                <SelectItem key={l.name} value={l.name} className="capitalize">
                  {l.name} ({l.stationcount})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(hasFilters || showAllPopular) && (
            <button
              data-testid="clear-filters-btn"
              onClick={clearAll}
              className="flex items-center gap-1.5 rounded-full border border-border bg-surface px-4 py-2 text-sm text-muted2 transition-colors hover:text-foreground"
            >
              <X size={16} /> Clear
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {selectedCountry ? (
            <>
              <button
                data-testid="language-chip-all"
                onClick={() => setLanguage("all")}
                className={`rounded-full border px-3.5 py-1.5 font-mono text-xs transition-all ${
                  language === "all"
                    ? "border-gold bg-gold text-black"
                    : "border-border bg-surface text-muted2 hover:border-white/20 hover:text-foreground"
                }`}
              >
                All languages
              </button>
              {countryLanguageChips.map((item) => (
                <button
                  key={item.name}
                  data-testid={`language-chip-${item.name}`}
                  onClick={() => setLanguage((prev) => (prev === item.name ? "all" : item.name))}
                  className={`rounded-full border px-3.5 py-1.5 font-mono text-xs capitalize transition-all ${
                    language === item.name
                      ? "border-gold bg-gold text-black"
                      : "border-border bg-surface text-muted2 hover:border-white/20 hover:text-foreground"
                  }`}
                >
                  {item.name} ({item.stationcount})
                </button>
              ))}
            </>
          ) : (
            COUNTRY_CHIPS.map((chip) => (
              <button
                key={chip.iso}
                data-testid={`country-chip-${chip.iso}`}
                onClick={() => handleCountryChip(chip.iso)}
                className={`rounded-full border px-3.5 py-1.5 font-mono text-xs transition-all ${
                  activeChipIso === chip.iso
                    ? "border-gold bg-gold text-black"
                    : "border-border bg-surface text-muted2 hover:border-white/20 hover:text-foreground"
                }`}
              >
                {chip.label}
              </button>
            ))
          )}
        </div>
      </section>

      {/* Recently Played */}
      {showSections && filteredHistory.length > 0 && (
        <section>
          <div className="mb-6 flex items-center gap-2">
            <ClockCounterClockwise size={22} weight="duotone" className="text-gold" />
            <h2 className="font-heading text-2xl font-medium tracking-tight text-foreground">
              Recently Played
            </h2>
          </div>
          <StationGrid
            stations={filteredHistory}
            loading={false}
            emptyState={
              <div className="rounded-2xl border border-border bg-surface py-16 text-center">
                <p className="font-heading text-xl text-foreground">No recent stations</p>
                <p className="mt-2 text-sm text-muted2">
                  Start listening to see your recently played stations here.
                </p>
              </div>
            }
          />
        </section>
      )}

      {/* Nearby Channels */}
      {showSections && userCountryAllowed && (
        <section>
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <MapPin size={22} weight="duotone" className="text-gold" />
              <h2 className="font-heading text-2xl font-medium tracking-tight text-foreground">
                Nearby Channels
              </h2>
            </div>
            <span className="flex items-center gap-1.5 rounded-full border border-border bg-surface px-3.5 py-1 font-mono text-xs text-muted2">
              <span>{getFlagEmoji(countryData.countryCode)}</span>
              <span>{countryData.countryName || countryData.countryCode}</span>
            </span>
          </div>
          <StationGrid
            stations={filteredNearby}
            loading={nearbyLoading}
            emptyState={
              <div className="rounded-2xl border border-border bg-surface py-16 text-center">
                <p className="font-heading text-xl text-foreground">No nearby stations found</p>
                <p className="mt-2 text-sm text-muted2">
                  We couldn't find stations for your location.
                </p>
              </div>
            }
          />
          {filteredNearby.length > 0 && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={handleSeeAllNearby}
                className="flex items-center gap-2 rounded-full border border-border bg-surface px-7 py-2.5 font-mono text-sm text-muted2 transition-all hover:border-gold/60 hover:text-gold"
              >
                See All from {countryData.countryName || countryData.countryCode}
                <ArrowRight size={15} />
              </button>
            </div>
          )}
        </section>
      )}

      {/* Most Popular Stations */}
      {showSections && (
        <section>
          <div className="mb-6 flex items-center gap-2">
            <Fire size={22} weight="duotone" className="text-gold" />
            <h2 className="font-heading text-2xl font-medium tracking-tight text-foreground">
              Most Popular Stations
            </h2>
          </div>
          <StationGrid
            stations={filteredPopular}
            loading={popularLoading}
            emptyState={emptyState}
          />
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleSeeAllPopular}
              className="flex items-center gap-2 rounded-full border border-border bg-surface px-7 py-2.5 font-mono text-sm text-muted2 transition-all hover:border-gold/60 hover:text-gold"
            >
              See All Stations
              <ArrowRight size={15} />
            </button>
          </div>
        </section>
      )}

      {/* Full Results */}
      {(hasFilters || showAllPopular) && (
        <section>
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Fire size={22} weight="duotone" className="text-gold" />
              <h2 className="font-heading text-2xl font-medium tracking-tight text-foreground">
                {hasFilters ? "Results" : "Most Popular Stations"}
              </h2>
              {stations.length > 0 && (
                <span className="font-mono text-sm text-muted2">({stations.length})</span>
              )}
            </div>
            {showAllPopular && !hasFilters && (
              <button
                onClick={() => setShowAllPopular(false)}
                className="flex items-center gap-1.5 font-mono text-sm text-muted2 transition-colors hover:text-foreground"
              >
                <X size={14} />
                Back
              </button>
            )}
          </div>
          <StationGrid
            stations={hasFilters && !showAllFiltered ? stations.slice(0, 8) : stations}
            loading={isLoading}
            emptyState={emptyState}
          />
          {hasFilters && stations.length > 8 && !showAllFiltered && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => setShowAllFiltered(true)}
                className="flex items-center gap-2 rounded-full border border-border bg-surface px-7 py-2.5 font-mono text-sm text-muted2 transition-all hover:border-gold/60 hover:text-gold"
              >
                See All Results
                <ArrowRight size={15} />
              </button>
            </div>
          )}
          {(!hasFilters || showAllFiltered) && (
            <>
              <div ref={loadMoreRef} className="h-10" />
              {isFetchingNextPage && (
                <div
                  className="flex items-center justify-center gap-2 py-6 font-mono text-sm text-muted2"
                  data-testid="loading-more"
                >
                  <Broadcast size={18} weight="fill" className="animate-pulse text-gold" />
                  Loading more stations...
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
